import { useCallback } from 'react';
import { useNativeEvent } from './useNativeEvent';
import { useInstanceId } from './useInstanceId';
import { useLogAnalyticsEvent } from './useLogAnalyticsEvent';
import { isIOS, isNotNil } from '../utils';

/**
 * - Native에서 scrollToTop 이벤트를 수신하여 콜백을 실행합니다.
 * - 탭 클릭 또는 Top 버튼 클릭 시 호출됩니다.
 * - CLICK_SCROLL_TO_TOP 이벤트도 함께 로깅합니다.
 *   (PR #1936 이전엔 RN의 HomeFloatingButtons에서 발화하던 것을 native floating
 *   전환으로 누락되어 훅에서 일괄 처리. native가 emit하는 경로가 button/탭 재선택
 *   모두 동일 이벤트라 탭 재선택 케이스가 false positive로 포함될 수 있음.)
 * - 이 훅이 호출되는 위치(Today/GoodsRanking/MarketRanking.screen)는 AnalyticsScreen
 *   하위가 아니라 SCREEN을 context로 못 얻으므로, 호출자가 screen prop으로 명시 전달.
 */
export const useNativeScrollToTop = ({
  onScrollToTop,
  screen,
}: {
  onScrollToTop: () => void;
  screen: string;
}) => {
  const instanceId = useInstanceId();
  const logAnalyticsEvent = useLogAnalyticsEvent();

  const handleScrollToTop = useCallback(() => {
    if (isIOS) {
      logAnalyticsEvent({
        channel: ['ablylog', 'amplitude', 'firebase'],
        name: 'CLICK_SCROLL_TO_TOP',
        ...(isNotNil(screen) &&
          screen !== '' && { analytics: { SCREEN: screen } }),
      });
    }
    onScrollToTop();
  }, [logAnalyticsEvent, onScrollToTop, screen]);

  useNativeEvent('scrollToTop', instanceId, handleScrollToTop);
};
